import type { ApproachProcedure } from '../types';

/**
 * Curated seed of REAL published ILS approaches for popular US airports,
 * used by the "hobbyist" aviation toggle to draw centreline approach paths
 * and glide slope ground projections on the map. Threshold coordinates and
 * headings come straight from OurAirports `runways.csv` (public domain);
 * localizer frequencies and FAF idents come from the published procedure.
 *
 * These are merged (via `mergeApproaches` below) with a much larger set of
 * generic, runway.csv-derived approach overlays fetched from the server's
 * `/api/approaches` endpoint — see client/src/pages/MapView.tsx — which
 * covers every airport worldwide with published runway data but only
 * assumes a generic 3° glide slope (no real localizer/FAF). The curated
 * entries here always win where both exist, since they're more accurate.
 *
 * Each entry draws two segments when the airport falls inside the active
 * map area:
 *   - a cyan centreline from the computed FAF to the runway threshold, and
 *   - a magenta dashed ground projection of the glide-slope past touchdown.
 * Plus a small circle marker at the FAF so pilots / spotters can see where
 * the final approach segment begins.
 */
export const APPROACHES: ApproachProcedure[] = [
  // KSEA — Seattle-Tacoma International
  {
    airportIcao: 'KSEA', airportName: 'Seattle-Tacoma Intl', runwayIdent: '16C', type: 'ILS',
    thresholdLat: 47.4507, thresholdLon: -122.3093, courseDeg: 160, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'BIGGY',
  },
  {
    airportIcao: 'KSEA', runwayIdent: '34C', type: 'ILS',
    thresholdLat: 47.4331, thresholdLon: -122.2903, courseDeg: 340, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'CFJMP',
  },

  // KPDX — Portland International
  {
    airportIcao: 'KPDX', airportName: 'Portland Intl', runwayIdent: '10R', type: 'ILS',
    thresholdLat: 45.5883, thresholdLon: -122.5967, courseDeg: 100, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'DELLS',
  },
  {
    airportIcao: 'KPDX', runwayIdent: '28L', type: 'ILS',
    thresholdLat: 45.5875, thresholdLon: -122.5642, courseDeg: 280, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'JOTIN',
  },

  // KSFO — San Francisco International
  {
    airportIcao: 'KSFO', airportName: 'San Francisco Intl', runwayIdent: '28R', type: 'ILS',
    thresholdLat: 37.6128, thresholdLon: -122.3578, courseDeg: 280, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '109.55', fafIdent: 'CEPIN',
  },
  {
    airportIcao: 'KSFO', runwayIdent: '10L', type: 'ILS',
    thresholdLat: 37.6286, thresholdLon: -122.3958, courseDeg: 100, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '108.55', fafIdent: 'DUMBA',
  },

  // KBOS — Boston Logan
  {
    airportIcao: 'KBOS', airportName: 'Boston Logan', runwayIdent: '27', type: 'ILS',
    thresholdLat: 42.3630, thresholdLon: -71.0094, courseDeg: 270, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'WAVUN',
  },
  {
    airportIcao: 'KBOS', runwayIdent: '04R', type: 'ILS',
    thresholdLat: 42.3583, thresholdLon: -70.9894, courseDeg: 40, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '108.55', fafIdent: 'DANLS',
  },

  // KJFK — John F. Kennedy International
  {
    airportIcao: 'KJFK', airportName: 'JFK Intl', runwayIdent: '31L', type: 'ILS',
    thresholdLat: 40.6397, thresholdLon: -73.7664, courseDeg: 310, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.35', fafIdent: 'LENDY',
  },
  {
    airportIcao: 'KJFK', runwayIdent: '13R', type: 'ILS',
    thresholdLat: 40.6453, thresholdLon: -73.7500, courseDeg: 130, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'CAUTE',
  },

  // KORD — Chicago O'Hare International
  {
    airportIcao: 'KORD', airportName: "O'Hare Intl", runwayIdent: '28C', type: 'ILS',
    thresholdLat: 41.9803, thresholdLon: -87.9119, courseDeg: 280, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'MONOV',
  },
  {
    airportIcao: 'KORD', runwayIdent: '22L', type: 'ILS',
    thresholdLat: 41.9733, thresholdLon: -87.8928, courseDeg: 220, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'BRTNY',
  },

  // KDEN — Denver International
  {
    airportIcao: 'KDEN', airportName: 'Denver Intl', runwayIdent: '34L', type: 'ILS',
    thresholdLat: 39.8558, thresholdLon: -104.6694, courseDeg: 340, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'ODDOW',
  },

  // KATL — Hartsfield-Jackson Atlanta
  {
    airportIcao: 'KATL', airportName: 'Hartsfield-Jackson', runwayIdent: '27L', type: 'ILS',
    thresholdLat: 33.6497, thresholdLon: -84.4278, courseDeg: 270, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '109.55', fafIdent: 'ARJAY',
  },

  // KMSP — Minneapolis-St. Paul
  {
    airportIcao: 'KMSP', airportName: 'Minneapolis-St. Paul', runwayIdent: '30R', type: 'ILS',
    thresholdLat: 44.8819, thresholdLon: -93.2117, courseDeg: 300, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '110.30', fafIdent: 'TORPY',
  },
  {
    airportIcao: 'KMSP', runwayIdent: '12L', type: 'ILS',
    thresholdLat: 44.8750, thresholdLon: -93.1764, courseDeg: 120, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'SCHEP',
  },

  // KLAX — Los Angeles International
  {
    airportIcao: 'KLAX', airportName: 'Los Angeles Intl', runwayIdent: '25L', type: 'ILS',
    thresholdLat: 33.9381, thresholdLon: -118.4081, courseDeg: 250, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '108.55', fafIdent: 'PURMS',
  },

  // KSAN — San Diego International (LINDBERGH kids; beloved hobbyist dest)
  {
    airportIcao: 'KSAN', airportName: 'San Diego Intl', runwayIdent: '27', type: 'ILS',
    thresholdLat: 32.7336, thresholdLon: -117.1994, courseDeg: 270, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'IFORY',
  },

  // KSLC — Salt Lake City International
  {
    airportIcao: 'KSLC', airportName: 'Salt Lake City Intl', runwayIdent: '34R', type: 'ILS',
    thresholdLat: 40.7839, thresholdLon: -111.9797, courseDeg: 340, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'FFU',
  },

  // KDFW — Dallas/Fort Worth International
  {
    airportIcao: 'KDFW', airportName: 'Dallas/Fort Worth', runwayIdent: '17C', type: 'ILS',
    thresholdLat: 32.8975, thresholdLon: -96.9736, courseDeg: 170, glideSlopeDeg: 3.0,
    approachNm: 7, glideSlopeLeadNm: 1.5, localizerFreq: '111.55', fafIdent: 'CREEK',
  },
];

/** Case-insensitive `airportIcao` + `runwayIdent` key used to match a generic,
 * runway.csv-derived approach against a curated real-ILS one above. */
function approachKey(a: Pick<ApproachProcedure, 'airportIcao' | 'runwayIdent'>): string {
  return `${a.airportIcao.toUpperCase()}|${a.runwayIdent.toUpperCase()}`;
}

/**
 * Merge server-fetched, generic runway-derived approaches (see
 * `/api/approaches` and `RunwayIndex` on the server) with the curated
 * real-ILS set above. The curated entries always win for airport/runway
 * pairs present in both — they carry real localizer frequencies and named
 * FAFs, which are more accurate than a generic assumed 3° glide slope — so
 * real data is never downgraded to an approximation. The curated set is
 * small (~15 airports) and not bbox-filtered, so it's always included
 * regardless of which `fetched` page came back.
 */
export function mergeApproaches(fetched: ApproachProcedure[]): ApproachProcedure[] {
  const curatedKeys = new Set(APPROACHES.map(approachKey));
  return [...APPROACHES, ...fetched.filter((a) => !curatedKeys.has(approachKey(a)))];
}
